// The rock an ore field is drawn as (§26 F3).
//
// Procedural rather than an asset, the reading the pawn's chevron and the
// artifact's octahedron already take: what is wanted is a *lump*, and a lump
// is a sphere somebody has knocked the smoothness off. An asset would be a
// file, a licence line and a load to wait on, for a shape with no detail in it.
//
// One geometry, instanced many times and jittered by *placement* rather than
// by shape — a rock that differed per instance would be one geometry per lump,
// which is the thing instancing exists to avoid.

import { BufferGeometry, IcosahedronGeometry } from "three";

/**
 * A low-poly rock: an icosahedron with every vertex pushed in or out.
 *
 * **Hashed off the vertex's own position, never rolled.** Two clients draw one
 * board and a board is redrawn on every frame the factory is busy, so a rolled
 * jitter would make each client's rocks a different shape and each frame's a
 * different shape again. It is also what lets this be built once at startup.
 *
 * `detail: 0` — twenty faces. Anything more is subdivision nobody can see at a
 * lump this size, paid for on every tile of every field on a grown board.
 */
export function createOreLumpGeometry(rough = 0.32): BufferGeometry {
  const geometry = new IcosahedronGeometry(0.5, 0);
  const points = geometry.getAttribute("position");

  for (let i = 0; i < points.count; i++) {
    const x = points.getX(i);
    const y = points.getY(i);
    const z = points.getZ(i);
    // Cheap integer mixing over the quantised position, the same shape
    // `scatter` uses for a wreck's yaw. Quantised because an icosahedron's
    // vertices are shared between faces and arrive as separate entries: two
    // that are the same corner must be pushed the same way, or the shell tears
    // open along every edge.
    const key = Math.round(x * 512) * 73856093 ^ Math.round(y * 512) * 19349663 ^ Math.round(z * 512) * 83492791;
    let h = Math.imul(key ^ 0x9e3779b9, 0x85ebca6b);
    h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    const push = 1 + (((h ^ (h >>> 16)) >>> 0) / 4294967296 - 0.5) * rough * 2;
    points.setXYZ(i, x * push, y * push, z * push);
  }

  points.needsUpdate = true;
  // Flat, so the facets catch light as facets — a smooth-shaded lump at this
  // size is a grey blob, which is what the kit's belt material was doing
  // before `flatShading` went on it.
  geometry.computeVertexNormals();
  return geometry;
}
